'use client';

import { downloadSignedUrls } from '@/lib/download/download';
import {
  DOWNLOAD_CANCELLED_MESSAGE,
  type ZipDownloadProgressEvent,
} from '@/lib/download/zip-download-core';
import {
  DownloadSignedUrlsResult,
  SignedUrlEntry,
  type ZipWorkerInMessage,
  type ZipWorkerOutMessage,
} from '@/types/download';
import { saveAs } from 'file-saver';
import { useCallback, useEffect, useRef } from 'react';

// eslint-disable-next-line no-unused-vars
type ProgressHandler = (progress: ZipDownloadProgressEvent) => void;

type UseZipDownloadWorkerOptions = {
  // eslint-disable-next-line no-unused-vars
  onInstantChange?: (instant: boolean) => void;
};

function createZipDownloadWorker(): Worker | null {
  if (typeof Worker === 'undefined') return null;
  try {
    return new Worker(new URL('../../workers/zip-download.worker.ts', import.meta.url), {
      type: 'module',
    });
  } catch {
    return null;
  }
}

export function useZipDownloadWorker({ onInstantChange }: UseZipDownloadWorkerOptions = {}) {
  const workerRef = useRef<Worker | null>(null);
  const lastProgressRef = useRef<ZipDownloadProgressEvent | null>(null);
  const onProgressRef = useRef<ProgressHandler | null>(null);
  // eslint-disable-next-line no-unused-vars
  const rejectRef = useRef<((error: Error) => void) | null>(null);
  const fallbackAbortControllerRef = useRef<AbortController | null>(null);
  const onInstantChangeRef = useRef(onInstantChange);
  onInstantChangeRef.current = onInstantChange;

  const terminateWorker = useCallback(() => {
    if (!workerRef.current) return;
    workerRef.current.terminate();
    workerRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    workerRef.current?.postMessage({ type: 'CANCEL' } satisfies ZipWorkerInMessage);
    terminateWorker();
    fallbackAbortControllerRef.current?.abort();
    fallbackAbortControllerRef.current = null;
    const reject = rejectRef.current;
    rejectRef.current = null;
    reject?.(new Error(DOWNLOAD_CANCELLED_MESSAGE));
  }, [terminateWorker]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        onInstantChangeRef.current?.(true);
        return;
      }

      const last = lastProgressRef.current;
      if (last) {
        onProgressRef.current?.(last);
      }
      onInstantChangeRef.current?.(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!document.hidden) {
            onInstantChangeRef.current?.(false);
          }
        });
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      cancel();
    };
  }, [cancel]);

  const start = useCallback(
    (
      signedUrls: SignedUrlEntry[],
      zipName: string,
      saveHandle: FileSystemFileHandle | null | undefined,
      onProgress: ProgressHandler,
    ): Promise<DownloadSignedUrlsResult | null> => {
      const worker = createZipDownloadWorker();
      if (!worker) {
        return Promise.resolve(null);
      }

      cancel();
      workerRef.current = worker;
      onProgressRef.current = onProgress;
      lastProgressRef.current = null;

      return new Promise<DownloadSignedUrlsResult | null>((resolve, reject) => {
        let retriedWithoutHandle = false;
        let cloneRetryInFlight = false;
        let settled = false;

        const settle = (fn: () => void) => {
          if (settled) return;
          settled = true;
          rejectRef.current = null;
          fn();
        };

        rejectRef.current = (error) => {
          settle(() => reject(error));
        };

        const postStart = (handle: FileSystemFileHandle | null | undefined) => {
          const message: ZipWorkerInMessage = {
            type: 'START',
            signedUrls,
            zipName,
            saveHandle: handle ?? undefined,
          };
          worker.postMessage(message);
        };

        const retryWithoutHandle = () => {
          if (cloneRetryInFlight) return;
          if (retriedWithoutHandle || !saveHandle) {
            settle(() => {
              terminateWorker();
              reject(new Error('No se pudo iniciar la descarga en el worker'));
            });
            return;
          }
          retriedWithoutHandle = true;
          cloneRetryInFlight = true;
          postStart(undefined);
        };

        worker.addEventListener('messageerror', () => {
          retryWithoutHandle();
        });

        worker.addEventListener('error', (event) => {
          settle(() => {
            terminateWorker();
            reject(new Error(event.message || 'Error en el worker de descarga'));
          });
        });

        worker.addEventListener('message', (event: MessageEvent<ZipWorkerOutMessage | null>) => {
          const data = event.data;
          if (data == null || data.type === 'CLONE_FAILED') {
            retryWithoutHandle();
            return;
          }

          if (data.type === 'PROGRESS') {
            cloneRetryInFlight = false;
            const progress = {
              downloaded: data.downloaded,
              failed: data.failed,
              total: data.totalFiles,
              phase: data.phase,
              bytesReceived: data.bytesReceived,
              totalBytes: data.totalBytes,
              percent: data.percent,
            };

            lastProgressRef.current = progress;
            onProgressRef.current?.(progress);
            return;
          }

          if (data.type === 'BLOB') {
            saveAs(data.blob, `${data.zipName}.zip`);
            settle(() => {
              terminateWorker();
              resolve({ downloaded: data.downloaded, failedCount: data.failedCount });
            });
            return;
          }

          if (data.type === 'DONE') {
            settle(() => {
              terminateWorker();
              resolve({ downloaded: data.downloaded, failedCount: data.failedCount });
            });
            return;
          }

          if (data.type === 'ERROR') {
            settle(() => {
              terminateWorker();
              reject(new Error(data.message));
            });
          }
        });

        postStart(saveHandle);
      });
    },
    [cancel, terminateWorker],
  );

  const startOrFallback = useCallback(
    async (
      signedUrls: SignedUrlEntry[],
      zipName: string,
      saveHandle: FileSystemFileHandle | null | undefined,
      onProgress: ProgressHandler,
    ): Promise<DownloadSignedUrlsResult> => {
      const workerResult = await start(signedUrls, zipName, saveHandle, onProgress);
      if (workerResult) {
        return workerResult;
      }
      const fallbackAbortController = new AbortController();
      fallbackAbortControllerRef.current = fallbackAbortController;
      try {
        return await downloadSignedUrls(
          signedUrls,
          zipName,
          (progress) => {
            onProgress({
              downloaded: progress.downloaded,
              failed: progress.failed,
              total: progress.total,
              phase: progress.phase,
              bytesReceived: progress.bytesReceived ?? 0,
              totalBytes: progress.totalBytes ?? null,
              percent: progress.percent ?? 0,
            });
          },
          saveHandle,
          fallbackAbortController.signal,
        );
      } finally {
        if (fallbackAbortControllerRef.current === fallbackAbortController) {
          fallbackAbortControllerRef.current = null;
        }
      }
    },
    [start],
  );

  return { start: startOrFallback, cancel };
}
